<script setup lang="ts">
import useHasPermissionUser from '@/composables/useHasPermissionUser'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import { useQueryClient } from '@tanstack/vue-query'
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import useRole from '../composables/useRole'
import CompanyRoleAbilitiesPanel from '@/modules/companies/components/CompanyRoleAbilitiesPanel.vue'
import GeneralTab from './partials/edit/GeneralTab.vue'
import LogTab from './partials/edit/LogTab.vue'
import MembersTab from './partials/edit/MembersTab.vue'

const route = useRoute()
const toast = useToast()
const queryClient = useQueryClient()

const { abilities, redirectTo404 } = useHasPermissionUser()

watch(abilities, () => { redirectTo404('update-roles', 'App\\Models\\Company') }, { immediate: true })

const { isLoading, role, updateRoleMutationAsync } = useRole({
  id: Number(route.params.id),
  immediate: true,
})

const handleSave = async (name: string) => {
  if (role.value?.name === name) {
    toast.info('No se detectaron cambios en el nombre del rol')
    return
  }
  if (!name.length) {
    toast.error('El nombre del rol es requerido')
    return
  }
  await updateRoleMutationAsync({ name })
  await queryClient.refetchQueries(['roles'])
}

const TAB_DEFS = [
  { name: 'General',    icon: 'tune' },
  { name: 'Permisos',   icon: 'lock' },
  { name: 'Miembros',   icon: 'group' },
  { name: 'Historial',  icon: 'history' },
]

const membersLabel = ref('Miembros')
watch(role, (r) => {
  if (r) membersLabel.value = `Miembros (${r.usersCount ?? 0})`
}, { immediate: true })
</script>

<template>
  <!-- Loading -->
  <div v-if="isLoading" class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4 animate-pulse">
    <div class="h-8 w-48 rounded-xl bg-gray-100" />
    <div class="h-32 rounded-xl bg-gray-100" />
    <div class="h-96 rounded-xl bg-gray-100" />
  </div>

  <div v-else-if="role" class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

    <!-- Back link -->
    <RouterLink
      :to="{ name: 'roles.index' }"
      class="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#021F58] mb-5 transition-colors"
    >
      <span class="material-symbols-outlined text-[18px]">arrow_back</span>
      Roles
    </RouterLink>

    <!-- Hero card -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-5">
      <div class="relative h-20 bg-gradient-to-br from-[#111d3a] via-[#253C70] to-[#3d6199]">
        <svg class="absolute inset-0 w-1/2 h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="role-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" stroke-width="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#role-grid)" />
        </svg>
      </div>

      <div class="flex items-end gap-4 -mt-8 relative z-10 px-6 pb-4">
        <div class="w-16 h-16 rounded-xl flex-shrink-0 bg-white shadow-md border border-gray-100 flex items-center justify-center">
          <span class="material-symbols-outlined text-[28px] text-[#021F58]">shield_person</span>
        </div>
        <div class="pb-1">
          <h1 class="text-xl font-bold text-gray-900">{{ role.name }}</h1>
          <p class="text-xs text-gray-500">{{ role.companyName ?? 'Rol del sistema' }} · {{ role.usersCount }} miembro{{ role.usersCount !== 1 ? 's' : '' }}</p>
        </div>
      </div>

      <!-- Tab bar -->
      <TabGroup as="div">
        <TabList as="div" class="flex items-center gap-1 px-4 py-2 border-t border-gray-100">
          <Tab
            v-for="(tab, i) in TAB_DEFS"
            :key="tab.name"
            v-slot="{ selected }"
            as="template"
          >
            <button
              :class="[
                'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-150 focus:outline-none',
                selected
                  ? 'bg-[#253C70] text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100',
              ]"
            >
              <span class="material-symbols-outlined text-[17px]">{{ tab.icon }}</span>
              <span class="hidden sm:inline">
                {{ i === 2 ? membersLabel : tab.name }}
              </span>
            </button>
          </Tab>
        </TabList>

        <div class="px-6 py-5">
          <TabPanels>
            <TabPanel>
              <GeneralTab :role="role" @handle-change-name="handleSave" />
            </TabPanel>
            <TabPanel>
              <CompanyRoleAbilitiesPanel :role="role" />
            </TabPanel>
            <TabPanel>
              <MembersTab :role="role" />
            </TabPanel>
            <TabPanel>
              <LogTab :role="role" />
            </TabPanel>
          </TabPanels>
        </div>
      </TabGroup>
    </div>

  </div>
</template>
